import type { BusinessCard } from './types';

type GltfResult = {
  json: any;
  uri: string;
};

const cardAspect = 1.75; // width / height for a single card face

declare const require: any;
const nodeCanvasFactory = (() => {
  try {
    if (typeof require !== 'undefined') {
      const mod = require('@napi-rs/canvas');
      if (mod?.createCanvas) return mod.createCanvas as (w: number, h: number) => any;
    }
  } catch {
    // Optional dependency; ignore if missing
  }
  return null;
})();

function toBase64(arrayBuffer: ArrayBuffer) {
  let binary = '';
  const bytes = new Uint8Array(arrayBuffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function drawFront(ctx: CanvasRenderingContext2D, card: BusinessCard, width: number, height: number) {
  // Background
  const gradient = ctx.createLinearGradient(0, 0, width, height * 0.9);
  gradient.addColorStop(0, '#0ea5e9');
  gradient.addColorStop(0.5, '#2563eb');
  gradient.addColorStop(1, '#0e1a34');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, width, height);

  // Accent glow
  const glow = ctx.createRadialGradient(width * 0.7, height * 0.15, 40, width * 0.65, height * 0.15, width * 0.45);
  glow.addColorStop(0, 'rgba(255,255,255,0.24)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  // Grid overlay
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  for (let x = 0; x < width; x += 90) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  // Header
  ctx.fillStyle = '#e2e8f0';
  ctx.font = '600 62px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText(card.name || 'Your Name', width * 0.08, height * 0.12);

  ctx.font = '500 32px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillStyle = 'rgba(226,232,240,0.9)';
  ctx.fillText(card.title || 'Role / Title', width * 0.08, height * 0.12 + 72);

  if (card.company) {
    ctx.font = '600 24px "Space Grotesk", "Inter", system-ui, sans-serif';
    ctx.fillStyle = 'rgba(226,232,240,0.72)';
    ctx.fillText(card.company, width * 0.08, height * 0.12 + 120);
  }

  // Center coordinate badge
  const coordY = height * 0.46;
  const locationLines = wrapLines(card.location?.address || 'Set your location to pin this card');
  const locationTop = coordY - 70;
  const locationBottom = coordY + 24 + (locationLines.length - 1) * 26 + 30;
  const locationHeight = locationBottom - locationTop;

  ctx.fillStyle = 'rgba(7,11,24,0.3)';
  ctx.beginPath();
  ctx.roundRect(width * 0.08, locationTop, width * 0.84, locationHeight, 24);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.stroke();

  const lat = card.location?.coordinates?.lat ?? 0;
  const lng = card.location?.coordinates?.lng ?? 0;
  ctx.font = '600 34px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillStyle = '#f8fafc';
  ctx.fillText(`Lat ${lat.toFixed(4)}`, width * 0.12, coordY - 12);

  ctx.textAlign = 'right';
  ctx.fillText(`Lng ${lng.toFixed(4)}`, width * 0.88, coordY - 12);
  ctx.textAlign = 'left';

  ctx.font = '500 22px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillStyle = 'rgba(226,232,240,0.82)';
  locationLines.forEach((line, i) => {
    ctx.fillText(line, width * 0.12, coordY + 24 + i * 24);
  });

  // Footer badges
  const badgeText = card.status === 'pending' ? 'Pending' : 'On-chain';
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.beginPath();
  ctx.roundRect(width * 0.08, height * 0.74, width * 0.36, 70, 18);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.stroke();
  ctx.fillStyle = '#fcd34d';
  ctx.beginPath();
  ctx.arc(width * 0.1 + 18, height * 0.74 + 35, 6.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#0b1120';
  ctx.font = '700 20px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillText(badgeText, width * 0.1 + 36, height * 0.74 + 20);
  ctx.fillStyle = 'rgba(15,23,42,0.8)';
  ctx.font = '500 16px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillText('Arkiv Mendoza', width * 0.1 + 36, height * 0.74 + 42);

  ctx.fillStyle = 'rgba(255,255,255,0.1)';
  ctx.beginPath();
  ctx.roundRect(width * 0.52, height * 0.74, width * 0.36, 70, 18);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.stroke();
  ctx.fillStyle = '#e2e8f0';
  ctx.font = '600 18px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillText(card.walletAddress ? `${card.walletAddress.slice(0, 6)}…${card.walletAddress.slice(-4)}` : '0xYourWallet', width * 0.54, height * 0.74 + 22);
  ctx.fillStyle = 'rgba(226,232,240,0.7)';
  ctx.font = '500 14px "Space Grotesk", "Inter", system-ui, sans-serif';
  ctx.fillText('Wallet', width * 0.54, height * 0.74 + 44);
}

function drawBack(ctx: CanvasRenderingContext2D, card: BusinessCard, width: number, height: number, offsetY: number) {
  ctx.save();
  ctx.translate(0, offsetY);

  ctx.fillStyle = 'rgb(243, 244, 246)';
  ctx.fillRect(0, 0, width, height);

  // Subtle glow bar
  const glow = ctx.createLinearGradient(0, 0, width, height);
  glow.addColorStop(0, 'rgba(255,255,255,0.14)');
  glow.addColorStop(0.45, 'rgba(255,255,255,0)');
  glow.addColorStop(1, 'rgba(255,255,255,0.24)');
  ctx.fillStyle = glow;
  ctx.fillRect(width * 0.08, height * 0.25, width * 0.84, height * 0.5);

  // Centered Arkiv logo
  const logo = '[ ARKIV ]';
  ctx.fillStyle = 'rgb(255, 107, 74)';
  ctx.font = '900 96px "Inter", system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(logo, width / 2, height / 2);
  ctx.textAlign = 'left';

  ctx.restore();
}

function escapeXml(input: string) {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function wrapLines(text: string, maxChars = 44, maxLines = 3) {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (next.length <= maxChars) {
      current = next;
      continue;
    }
    if (lines.length + 1 === maxLines) {
      lines.push(`${next.slice(0, maxChars - 1)}…`);
      return lines;
    }
    lines.push(current);
    current = word;
  }
  if (current) lines.push(current);
  return lines.slice(0, maxLines);
}

function createCardSvgTexture(card: BusinessCard, width: number, height: number) {
  const totalHeight = height * 2;
  const lat = card.location?.coordinates?.lat ?? 0;
  const lng = card.location?.coordinates?.lng ?? 0;
  const safeName = escapeXml(card.name || 'Your Name');
  const safeTitle = escapeXml(card.title || 'Role / Title');
  const safeCompany = escapeXml(card.company || '');
  const locationLines = wrapLines(card.location?.address || 'Set your location to pin this card');
  const safeAddressLines = locationLines.map(escapeXml);
  const locationBoxHeight = 120 + Math.max(0, locationLines.length - 1) * 28;
  const safeEmail = escapeXml(card.email || '');
  const safePhone = escapeXml(card.phone || '');
  const safeWebsite = escapeXml(card.website || '');
  const safeWallet = escapeXml(card.walletAddress ? `${card.walletAddress.slice(0, 6)}...${card.walletAddress.slice(-4)}` : '0xYourWallet');
  const badgeText = card.status === 'pending' ? 'Pending' : 'On-chain';

  const socials = [
    card.social?.linkedin ? `<g transform="translate(60, ${height * 0.64})"><rect width="210" height="56" rx="14" fill="rgba(255,255,255,0.9)" /><text x="24" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="18" font-weight="700" fill="#0f172a">IN</text><text x="60" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="15" font-weight="500" fill="#0f172a">${escapeXml(card.social.linkedin)}</text></g>` : '',
    card.social?.twitter ? `<g transform="translate(285, ${height * 0.64})"><rect width="210" height="56" rx="14" fill="rgba(255,255,255,0.9)" /><text x="24" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="18" font-weight="700" fill="#0f172a">X</text><text x="60" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="15" font-weight="500" fill="#0f172a">${escapeXml(card.social.twitter)}</text></g>` : '',
    card.social?.github ? `<g transform="translate(510, ${height * 0.64})"><rect width="210" height="56" rx="14" fill="rgba(255,255,255,0.9)" /><text x="24" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="18" font-weight="700" fill="#0f172a">GH</text><text x="60" y="36" font-family="Space Grotesk, Inter, system-ui" font-size="15" font-weight="500" fill="#0f172a">${escapeXml(card.social.github)}</text></g>` : '',
  ].join('');

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${totalHeight}" viewBox="0 0 ${width} ${totalHeight}">
<defs>
  <linearGradient id="frontGradient" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="#0ea5e9"/>
    <stop offset="55%" stop-color="#2563eb"/>
    <stop offset="100%" stop-color="#0e1a34"/>
  </linearGradient>
  <linearGradient id="backGradient" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stop-color="rgb(243, 244, 246)"/>
    <stop offset="100%" stop-color="rgb(243, 244, 246)"/>
  </linearGradient>
  <radialGradient id="frontGlow" cx="70%" cy="18%" r="60%">
    <stop offset="0%" stop-color="rgba(255,255,255,0.32)"/>
    <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
  </radialGradient>
  <linearGradient id="logoGlow" x1="0" y1="0" x2="1" y2="1">
    <stop offset="0%" stop-color="rgba(255,255,255,0.14)"/>
    <stop offset="45%" stop-color="rgba(255,255,255,0)"/>
    <stop offset="100%" stop-color="rgba(255,255,255,0.24)"/>
  </linearGradient>
</defs>
<!-- Front -->
<g>
  <rect width="${width}" height="${height}" fill="url(#frontGradient)"/>
  <rect width="${width}" height="${height}" fill="url(#frontGlow)"/>
  <g opacity="0.08" stroke="white" stroke-width="1">
    ${Array.from({ length: Math.ceil(width / 90) + 1 }).map((_, i) => `<line x1="${i * 90}" y1="0" x2="${i * 90}" y2="${height}" />`).join('')}
  </g>
  <text x="${width * 0.08}" y="${height * 0.12}" fill="#e2e8f0" font-family="Space Grotesk, Inter, system-ui" font-size="62" font-weight="600">${safeName}</text>
  <text x="${width * 0.08}" y="${height * 0.12 + 74}" fill="rgba(226,232,240,0.9)" font-family="Space Grotesk, Inter, system-ui" font-size="32" font-weight="500">${safeTitle}</text>
  ${safeCompany ? `<text x="${width * 0.08}" y="${height * 0.12 + 120}" fill="rgba(226,232,240,0.72)" font-family="Space Grotesk, Inter, system-ui" font-size="24" font-weight="600">${safeCompany}</text>` : ''}
  <rect x="${width * 0.08}" y="${height * 0.46 - 70}" width="${width * 0.84}" height="${locationBoxHeight}" rx="24" fill="rgba(7,11,24,0.3)" stroke="rgba(255,255,255,0.2)"/>
  <text x="${width * 0.12}" y="${height * 0.46 - 12}" fill="#f8fafc" font-family="Space Grotesk, Inter, system-ui" font-size="34" font-weight="600">Lat ${lat.toFixed(4)}</text>
  <text x="${width * 0.88}" y="${height * 0.46 - 12}" fill="#f8fafc" font-family="Space Grotesk, Inter, system-ui" font-size="34" font-weight="600" text-anchor="end">Lng ${lng.toFixed(4)}</text>
  <text x="${width * 0.12}" y="${height * 0.46 + 26}" fill="rgba(226,232,240,0.82)" font-family="Space Grotesk, Inter, system-ui" font-size="22" font-weight="500">
    ${safeAddressLines.map((line, i) => `<tspan x="${width * 0.12}" dy="${i === 0 ? 0 : 24}">${line}</tspan>`).join('')}
  </text>
  <g>
    <rect x="${width * 0.08}" y="${height * 0.74}" width="${width * 0.36}" height="70" rx="18" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.2)"/>
    <circle cx="${width * 0.1 + 18}" cy="${height * 0.74 + 35}" r="6.5" fill="#fcd34d"/>
    <text x="${width * 0.1 + 36}" y="${height * 0.74 + 20}" fill="#0b1120" font-family="Space Grotesk, Inter, system-ui" font-size="20" font-weight="700">${badgeText}</text>
    <text x="${width * 0.1 + 36}" y="${height * 0.74 + 44}" fill="rgba(15,23,42,0.8)" font-family="Space Grotesk, Inter, system-ui" font-size="16" font-weight="500">Arkiv Mendoza</text>
  </g>
  <g>
    <rect x="${width * 0.52}" y="${height * 0.74}" width="${width * 0.36}" height="70" rx="18" fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.2)"/>
    <text x="${width * 0.54}" y="${height * 0.74 + 22}" fill="#e2e8f0" font-family="Space Grotesk, Inter, system-ui" font-size="18" font-weight="600">${safeWallet}</text>
    <text x="${width * 0.54}" y="${height * 0.74 + 44}" fill="rgba(226,232,240,0.7)" font-family="Space Grotesk, Inter, system-ui" font-size="14" font-weight="500">Wallet</text>
  </g>
</g>
<!-- Back -->
<g transform="translate(0, ${height})">
  <rect width="${width}" height="${height}" fill="url(#backGradient)"/>
  <rect x="${width * 0.08}" y="${height * 0.25}" width="${width * 0.84}" height="${height * 0.5}" fill="url(#logoGlow)" />
  <text x="${width / 2}" y="${height / 2}" fill="rgb(255, 107, 74)" font-family="Inter, system-ui, sans-serif" font-size="96" font-weight="900" text-anchor="middle" dominant-baseline="middle">[ ARKIV ]</text>
</g>
</svg>
`;

  const base64 = typeof btoa !== 'undefined'
    ? btoa(unescape(encodeURIComponent(svg)))
    : typeof Buffer !== 'undefined'
      ? Buffer.from(svg, 'utf-8').toString('base64')
      : (() => { throw new Error('No encoder available for SVG texture'); })();
  return `data:image/svg+xml;base64,${base64}`;
}

function createCardTexture(card: BusinessCard, width = 1200, height = Math.floor(1200 / cardAspect)) {
  const getCanvas = () => {
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      return { canvas, ctx: canvas.getContext('2d') as CanvasRenderingContext2D | null, toDataURL: canvas.toDataURL.bind(canvas) };
    }
    if (nodeCanvasFactory) {
      const canvas = nodeCanvasFactory(width, height * 2);
      return { canvas, ctx: canvas.getContext('2d'), toDataURL: canvas.toDataURL.bind(canvas) };
    }
    return null;
  };

  const canvasBundle = getCanvas();
  if (!canvasBundle) {
    throw new Error('No canvas renderer available. Install optional dependency @napi-rs/canvas for headless exports.');
  }

  const { canvas, ctx, toDataURL } = canvasBundle;
  canvas.width = width;
  canvas.height = height * 2; // atlas: top = front, bottom = back
  if (!ctx) {
    throw new Error('Unable to create canvas context. Ensure @napi-rs/canvas is installed for CLI use.');
  }

  drawFront(ctx, card, width, height);
  drawBack(ctx, card, width, height, height);

  return toDataURL('image/png');
}

export function cardToGltf(card: BusinessCard): GltfResult {
  const textureData = createCardTexture(card);

  const halfThickness = 0.008;
  // Geometry: two faces (front/back) slightly separated to avoid z-fighting
  const positions = new Float32Array([
    // front face (z positive)
    -0.9, -0.55, halfThickness,
    0.9, -0.55, halfThickness,
    0.9, 0.55, halfThickness,
    -0.9, 0.55, halfThickness,
    // back face (z negative)
    -0.9, -0.55, -halfThickness,
    0.9, -0.55, -halfThickness,
    0.9, 0.55, -halfThickness,
    -0.9, 0.55, -halfThickness,
  ]);
  const uvs = new Float32Array([
    // front maps to top half of atlas
    0, 0.5,
    1, 0.5,
    1, 0,
    0, 0,
    // back maps to bottom half
    1, 1,
    0, 1,
    0, 0.5,
    1, 0.5,
  ]);
  const indices = new Uint16Array([
    0, 1, 2, 0, 2, 3, // front
    4, 6, 5, 4, 7, 6, // back (orientation flipped to face outward)
  ]);

  // Pack buffers into one ArrayBuffer for simplicity
  const positionBytes = positions.byteLength;
  const uvBytes = uvs.byteLength;
  const indexBytes = indices.byteLength;
  const totalBytes = positionBytes + uvBytes + indexBytes;
  const alignedTotal = Math.ceil(totalBytes / 4) * 4;

  const buffer = new ArrayBuffer(alignedTotal);
  const positionView = new Float32Array(buffer, 0, positions.length);
  positionView.set(positions);
  const uvView = new Float32Array(buffer, positionBytes, uvs.length);
  uvView.set(uvs);
  const indexView = new Uint16Array(buffer, positionBytes + uvBytes, indices.length);
  indexView.set(indices);

  const bufferUri = `data:application/octet-stream;base64,${toBase64(buffer)}`;

  const gltf = {
    asset: { version: '2.0', generator: 'globe-app-card' },
    buffers: [{ uri: bufferUri, byteLength: alignedTotal }],
    bufferViews: [
      { buffer: 0, byteOffset: 0, byteLength: positionBytes, target: 34962 },
      { buffer: 0, byteOffset: positionBytes, byteLength: uvBytes, target: 34962 },
      { buffer: 0, byteOffset: positionBytes + uvBytes, byteLength: indexBytes, target: 34963 },
    ],
    accessors: [
      { bufferView: 0, componentType: 5126, count: 8, type: 'VEC3', max: [0.9, 0.55, halfThickness], min: [-0.9, -0.55, -halfThickness] },
      { bufferView: 1, componentType: 5126, count: 8, type: 'VEC2' },
      { bufferView: 2, componentType: 5123, count: 12, type: 'SCALAR' },
    ],
    images: [{ uri: textureData }],
    samplers: [{ minFilter: 9729, magFilter: 9729, wrapS: 10497, wrapT: 10497 }],
    textures: [{ sampler: 0, source: 0 }],
    materials: [
      {
        pbrMetallicRoughness: {
          baseColorTexture: { index: 0 },
          metallicFactor: 0.0,
          roughnessFactor: 0.9,
        },
        doubleSided: true,
      },
    ],
    meshes: [
      {
        primitives: [
          {
            attributes: { POSITION: 0, TEXCOORD_0: 1 },
            indices: 2,
            material: 0,
          },
        ],
      },
    ],
    nodes: [{ mesh: 0 }],
    scenes: [{ nodes: [0] }],
    scene: 0,
  };

  return {
    json: gltf,
    uri: `data:application/json;base64,${toBase64(new TextEncoder().encode(JSON.stringify(gltf)).buffer)}`,
  };
}
